// StateFilters — the state select, the two "waiting on" shortcuts, Blocked,
// the search box and (on the archive view only) a date range. §6.2.
//
// The two shortcuts ARE what the Agreements page's saved views meant: "Waiting
// on WHP" was phase `countersign`, "Out for signature" was phase `out`. They
// are expressed as `waitingOn.party` now because that is the question actually
// being asked — is this ours to move, or theirs — and it answers correctly for
// a draft (ours) and a system sync (neither), which a phase filter never did.
//
// Everything here is CONTROLLED: this component owns no filter state except
// the search box's in-flight text, which is debounced so typing does not spam
// the history stack. The URL is the single source of truth (§6.2) and the page
// owns it.

import { useEffect, useState } from 'react';
import { humanizeDocState } from '@/lib/viewDocs';
import type { SentItemsFilterPatch } from '@/components/sentItems/sentItemsView';
import type {
  SentItemFacet,
  SentItemsListParams,
  SentItemsView,
  ViewDocState,
  WaitingParty,
} from '@/lib/sentItemsClient';

const STATE_OPTIONS: readonly ViewDocState[] = [
  'draft',
  'sent',
  'in_progress',
  'completed',
  'declined',
  'cancelled',
  'voided',
  'superseded',
];

const PARTY_SHORTCUTS: ReadonlyArray<{ party: WaitingParty; label: string; title: string }> = [
  {
    party: 'whp',
    label: 'Waiting on WHP',
    title: 'Ours to move: drafts, countersignatures, anything held here.',
  },
  {
    party: 'recipient',
    label: 'Waiting on a person',
    title: 'Out with a resident, applicant, landlord or owner.',
  },
];

function stateLabelFor(state: ViewDocState, facets: SentItemFacet[] | undefined): string {
  const hit = facets?.find((f) => f.value === state);
  return hit?.label?.trim() || humanizeDocState(state);
}

function stateCountFor(state: ViewDocState, facets: SentItemFacet[] | undefined): number | null {
  return facets?.find((f) => f.value === state)?.count ?? null;
}

export function StateFilters({
  params,
  view,
  stateFacets,
  onChange,
}: {
  params: SentItemsListParams;
  view: SentItemsView;
  stateFacets?: SentItemFacet[];
  onChange: (patch: SentItemsFilterPatch) => void;
}): React.JSX.Element {
  // The search box is the one control with local state: the URL is updated on
  // a 200ms trailing edge so a five-letter name is one history entry, not five.
  const [q, setQ] = useState(params.q ?? '');
  useEffect(() => {
    setQ(params.q ?? '');
  }, [params.q]);
  useEffect(() => {
    if (q === (params.q ?? '')) return;
    const t = setTimeout(() => onChange({ q }), 200);
    return () => clearTimeout(t);
  }, [q, params.q, onChange]);

  return (
    <div className="filter-bar si-filters">
      <input
        type="search"
        className="input si-search"
        placeholder="Search person, property or type"
        aria-label="Search everything sent, by person, property or type"
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />

      <select
        className="input si-state-select"
        aria-label="Filter by state"
        value={params.state ?? ''}
        onChange={(e) => {
          const v = e.target.value;
          onChange({ state: v ? (v as ViewDocState) : undefined });
        }}
      >
        <option value="">Every state</option>
        {STATE_OPTIONS.map((s) => {
          const count = stateCountFor(s, stateFacets);
          return (
            <option key={s} value={s}>
              {stateLabelFor(s, stateFacets)}
              {count != null ? ` (${count})` : ''}
            </option>
          );
        })}
      </select>

      {PARTY_SHORTCUTS.map((shortcut) => {
        const on = params.party === shortcut.party;
        return (
          <button
            key={shortcut.party}
            type="button"
            className="btn"
            data-variant={on ? 'secondary' : 'ghost'}
            data-size="sm"
            aria-pressed={on}
            title={shortcut.title}
            onClick={() => onChange({ party: on ? undefined : shortcut.party })}
          >
            {shortcut.label}
          </button>
        );
      })}

      <button
        type="button"
        className="btn"
        data-variant={params.blocked ? 'secondary' : 'ghost'}
        data-size="sm"
        aria-pressed={Boolean(params.blocked)}
        title="Something must be fixed before this moves: a failed sync, an exception, a blocking signal."
        onClick={() => onChange({ blocked: params.blocked ? undefined : true })}
      >
        Blocked
      </button>

      {/* The archive only. On the two in-motion views a date range would be
          filtering a set defined by "still moving", which is a question about
          now — the range would silently fight the view. */}
      {view === 'all' && (
        <span className="si-daterange">
          <label className="oc-meta" htmlFor="si-from">
            Between
          </label>
          <input
            id="si-from"
            type="date"
            className="input"
            aria-label="Show items active on or after"
            value={params.from ?? ''}
            onChange={(e) => onChange({ from: e.target.value || undefined })}
          />
          <span className="oc-meta">and</span>
          <input
            type="date"
            className="input"
            aria-label="Show items active on or before"
            value={params.to ?? ''}
            onChange={(e) => onChange({ to: e.target.value || undefined })}
          />
        </span>
      )}
    </div>
  );
}
